import React from 'react';
import {
  Badge,
  Box,
  HStack,
  IconButton,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { FiChevronLeft, FiChevronRight, FiList } from 'react-icons/fi';
import { RemoteSession, RemoteSessionStatus } from '../types';

interface Props {
  sessions: RemoteSession[];
  loading: boolean;
  total: number;
  page: number;
  limit?: number;
  onViewLogs: (sessionId: string) => void;
  onPageChange: (page: number) => void;
}

const STATUS_COLOR: Record<RemoteSessionStatus, string> = {
  pending: 'yellow',
  active: 'green',
  completed: 'blue',
  expired: 'gray',
  cancelled: 'red',
};

const fmt = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('id-ID', { dateStyle: 'short', timeStyle: 'short' }) : '-';

const SaktiRemoteSessionList: React.FC<Props> = ({
  sessions, loading, total, page, limit = 20, onViewLogs, onPageChange,
}) => {
  const totalPages = Math.ceil(total / limit);

  if (loading) {
    return <Box>{[1, 2, 3].map((i) => <Skeleton key={i} height="40px" mb={2} />)}</Box>;
  }

  if (sessions.length === 0) {
    return (
      <Box p={6} textAlign="center">
        <Text fontSize="sm" color="gray.400">Belum ada riwayat sesi.</Text>
      </Box>
    );
  }

  return (
    <Box>
      <Box overflowX="auto" borderWidth="1px" borderColor="gray.200" borderRadius="md">
        <Table size="sm" variant="simple">
          <Thead bg="gray.50">
            <Tr>
              <Th fontSize="xs">Status</Th>
              <Th fontSize="xs">NIP</Th>
              <Th fontSize="xs">Perangkat</Th>
              <Th fontSize="xs">Dibuat</Th>
              <Th fontSize="xs">Terhubung</Th>
              <Th fontSize="xs">Ditutup</Th>
              <Th fontSize="xs"></Th>
            </Tr>
          </Thead>
          <Tbody>
            {sessions.map((s) => (
              <Tr key={s.id} _hover={{ bg: 'gray.50' }}>
                <Td>
                  <Badge colorScheme={STATUS_COLOR[s.status]} fontSize="0.65rem">{s.status}</Badge>
                </Td>
                <Td fontSize="xs" fontFamily="mono">{s.nip ?? '-'}</Td>
                <Td fontSize="xs">{s.device_model ?? '-'}</Td>
                <Td fontSize="xs" whiteSpace="nowrap">{fmt(s.created_at)}</Td>
                <Td fontSize="xs" whiteSpace="nowrap">{fmt(s.connected_at)}</Td>
                <Td fontSize="xs" whiteSpace="nowrap">{fmt(s.closed_at)}</Td>
                <Td>
                  <IconButton
                    aria-label="Lihat log"
                    icon={<FiList />}
                    size="xs"
                    variant="ghost"
                    colorScheme="blue"
                    onClick={() => onViewLogs(s.id)}
                  />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      {totalPages > 1 && (
        <HStack justify="flex-end" mt={3} spacing={2}>
          <IconButton aria-label="Prev" icon={<FiChevronLeft />} size="xs" isDisabled={page <= 1} onClick={() => onPageChange(page - 1)} />
          <Text fontSize="xs" color="gray.600">{page} / {totalPages}</Text>
          <IconButton aria-label="Next" icon={<FiChevronRight />} size="xs" isDisabled={page >= totalPages} onClick={() => onPageChange(page + 1)} />
        </HStack>
      )}
    </Box>
  );
};

export default SaktiRemoteSessionList;
